import { useCallback } from "react";
import {
  startExtractImagesFromUrls,
  startSaveMediaInStorage,
} from "../../../actions/user";
import { validateBlockedBrandByPlanStatus } from "../../../pages/Settings/BrandSetup/utils/blockedBrandsValidation";
import { needsStoragePersistForImageUrl } from "../../../utils/string";

export const useGalleryFromWebsiteUrl = ({
  dispatch,
  fetchUrl,
  isPlanActive,
  isTrial,
  pathStorage,
  setData,
  setError,
  setIsLoading,
  setIsUploading,
}) => {
  const onGetInitialData = useCallback(async () => {
    setError(null);
    setData([]);

    const canExtractFromUrl = validateBlockedBrandByPlanStatus({
      value: fetchUrl,
      isPlanActive,
      isTrial,
    });
    if (!canExtractFromUrl) {
      setError("The request could not be processed.");
      return;
    }

    setIsLoading(true);
    const response = await dispatch(
      startExtractImagesFromUrls({ url: fetchUrl })
    );
    if (response.ok) {
      const images = response.data || [];
      const dataFormatted = images.map((image) => ({
        url: image,
        from: "url",
        id: image,
      }));
      setData(dataFormatted);
    } else {
      setError(response.message);
    }
    setIsLoading(false);
  }, [
    dispatch,
    fetchUrl,
    isPlanActive,
    isTrial,
    setData,
    setError,
    setIsLoading,
  ]);

  const onFailedLoadUrl = useCallback(
    (index, setSelectedItems) => {
      setData((prev) => {
        const newData = [...prev];
        const itemToRemove = newData[index];

        if (itemToRemove?.id) {
          setSelectedItems((prevSelected) =>
            prevSelected.filter((id) => id !== itemToRemove.id)
          );
        }

        return newData.filter((_, i) => i !== index);
      });
    },
    [setData]
  );

  const onSendSelectedItems = useCallback(
    async (itemsToProcess, { onCallbackSelectedItems, onCloseModal }) => {
      if (!itemsToProcess.length) return;

      const itemsNeedingPersist = itemsToProcess.filter((item) =>
        needsStoragePersistForImageUrl(item?.url)
      );

      const storedUrlByOriginal = new Map();

      if (itemsNeedingPersist.length > 0) {
        if (!pathStorage) {
          setError("The request could not be processed.");
          return;
        }

        setIsUploading(true);
        setError(null);

        const storageResponse = await dispatch(
          startSaveMediaInStorage({
            urls: itemsNeedingPersist.map((item) => item.url),
            path: pathStorage,
          })
        );

        setIsUploading(false);

        const storedUrls = storageResponse?.data?.urls || [];
        if (
          !storageResponse?.ok ||
          storedUrls.length !== itemsNeedingPersist.length
        ) {
          setError(
            storageResponse?.message || "The request could not be processed."
          );
          return;
        }

        itemsNeedingPersist.forEach((item, index) => {
          storedUrlByOriginal.set(item.url, storedUrls[index]);
        });
      }

      const dataSelected = itemsToProcess.map((item) => ({
        url: storedUrlByOriginal.get(item.url) || item.url,
        from: item.from,
        id: item.id,
        userID: item.userID,
        isCarousel: item.isCarousel,
        pageId: item.pageId,
        adId: item.adId,
        base64: item.base64,
      }));

      onCallbackSelectedItems?.(dataSelected);
      onCloseModal?.();
    },
    [dispatch, pathStorage, setError, setIsUploading]
  );

  return {
    onGetInitialData,
    onFailedLoadUrl,
    onSendSelectedItems,
  };
};
